import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import {
  Form,
  Formik,
  FormikProps,
} from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { updateEngineer } from '../models/engineer';
import { useAuth } from './Authentication';
import ButtonWithLoading from './ButtonWithLoading';
import { useEngineer } from './EngineerProvider';
import { FileField, TextField } from './Fields';
import LoadingPage from './Loading/Page';
import useResumeAttachment from './profile/useResumeAttachment';

interface AttachResumeProps {
  open?: boolean;
  onClose: () => void
}

const useStyles = makeStyles((theme) => ({
  form: {
    marginTop: theme.spacing(1),
  },
  formFields: {
    '& > *': {
      marginBottom: theme.spacing(2),
    },
  },
}));

export const validationSchema = Yup.object().shape({
  linkedIn: Yup.string().url().when('cvFile', {
    is: (file: any) => !file || file.length === 0,
    then: Yup.string().url().required('Please submit either your LinkedIn or resume'),
    otherwise: Yup.string().url(),
  }),
  cvFile: Yup.mixed().when('linkedIn', {
    is: (linkedIn: string) => linkedIn?.length > 0,
    then: Yup.mixed(),
    otherwise: Yup.mixed().test({ test: (file: any) => file && file.size > 0, message: 'Please submit either your LinkedIn or resume' }),
  }),
}, [['linkedIn', 'cvFile']]);

export default function AttachResume({ open, onClose }: AttachResumeProps) {
  const classes = useStyles();
  const { user } = useAuth();
  const { engineer, fetching } = useEngineer();
  const { upload: uploadResume } = useResumeAttachment(engineer);
  if (fetching || !engineer) return <LoadingPage />;
  const initialValues = {
    linkedIn: engineer?.linkedIn || '',
    cvFile: null as ReturnType<NonNullable<HTMLInputElement['files']>['item']>,
  };
  return (
    <Dialog open={Boolean(open)} onClose={onClose} aria-labelledby="attach-resume-dialog">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async ({ cvFile, linkedIn }) => {
          const s3Object = cvFile?.size
            ? await uploadResume(user!.identityId, cvFile)
            : undefined;
          await updateEngineer({ id: engineer.id, linkedIn, cv: s3Object });
          onClose();
        }}
      >
        {({ isSubmitting }: FormikProps<typeof initialValues>) => (
          <Form className={classes.form}>
            <DialogTitle id="attach-resume-dialog">Attach your resume</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Upload a document with your resume and fill in your LinkedIn URL.
                At least one of them is required
              </DialogContentText>
              <Box className={classes.formFields}>
                <TextField name="linkedIn" label="LinkedIn URL" fullWidth />
                <FileField name="cvFile" label="Resume" fullWidth />
              </Box>
            </DialogContent>
            <DialogActions>
              <ButtonWithLoading type="submit" color="primary" loading={isSubmitting}>
                Submit
              </ButtonWithLoading>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
